// Document metadata is set explicitly here and nowhere else — never derived
// from the project or prompt name.
export const SITE_TITLE = 'Lentic — Assessment for AI-assisted work'
export const SITE_DESCRIPTION =
  'Lentic runs candidates through an instrumented task where the AI is provided but the source documents are withheld, then produces the auditable record of how each person exercised judgement.'
export const SITE_OG_IMAGE = 'https://cdn.magicpatterns.com/uploads/wc7gr9wntPD888sQ3UFAgA/image.png'

// The official horizontal lockup asset. The favicon is cropped from this file,
// never redrawn.
export const LENTIC_LOCKUP =
  'https://cdn.magicpatterns.com/uploads/qmgzPPQc2zwfZy1MooT1VQ/lentic-logo-horizontal-teal-navy.svg'

function setMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(selector)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function setIcon(rel: string, href: string, type?: string) {
  document.head
    .querySelectorAll(`link[rel="${rel}"]`)
    .forEach((node) => node.parentElement?.removeChild(node))
  const link = document.createElement('link')
  link.setAttribute('rel', rel)
  if (type) link.setAttribute('type', type)
  link.setAttribute('href', href)
  document.head.appendChild(link)
}

function applyFavicon(href: string, type?: string) {
  setIcon('icon', href, type)
  setIcon('shortcut icon', href, type)
  setIcon('apple-touch-icon', href)
}

// Isolates the circular ripple symbol from the lockup: the mark occupies a square
// at the left edge, the height of the artwork. Cropped, not reinterpreted — no
// geometry, proportions or colours are altered, and the background stays transparent.
function cropMarkFromLockup() {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.onload = () => {
    const height = img.naturalHeight || img.height
    const width = img.naturalWidth || img.width
    if (!height || !width) return

    const side = Math.min(height, width)
    const size = 128
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Source square from the left edge of the lockup — the symbol, no wordmark.
    ctx.drawImage(img, 0, 0, side, side, 0, 0, size, size)

    try {
      applyFavicon(canvas.toDataURL('image/png'), 'image/png')
    } catch {
      // Canvas tainted by CORS — the lockup file itself still renders in the tab.
      applyFavicon(LENTIC_LOCKUP, 'image/svg+xml')
    }
  }
  img.onerror = () => applyFavicon(LENTIC_LOCKUP, 'image/svg+xml')
  img.src = LENTIC_LOCKUP
}

export function applyDocumentMeta() {
  if (typeof document === 'undefined') return

  document.title = SITE_TITLE

  setMeta('meta[name="description"]', 'name', 'description', SITE_DESCRIPTION)

  setMeta('meta[property="og:title"]', 'property', 'og:title', SITE_TITLE)
  setMeta('meta[property="og:description"]', 'property', 'og:description', SITE_DESCRIPTION)
  setMeta('meta[property="og:image"]', 'property', 'og:image', SITE_OG_IMAGE)
  setMeta('meta[property="og:type"]', 'property', 'og:type', 'website')

  setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image')
  setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', SITE_TITLE)
  setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', SITE_DESCRIPTION)
  setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', SITE_OG_IMAGE)

  cropMarkFromLockup()
}
